import { useEffect, useRef, useState } from 'react';

/**
 * useRevealOnScroll
 * Applies a `.am-reveal` → `.am-reveal-in` class swap to a DOM node when
 * it scrolls into view. Uses a shared IntersectionObserver so 1000+ cards
 * won't spawn 1000+ observers. `once=true` (default) unobserves after the
 * first reveal so no ongoing work.
 *
 * Consumer usage:
 *   const ref = useRevealOnScroll();
 *   return <div ref={ref} className="am-reveal">…</div>;
 */
export function useRevealOnScroll(options = {}) {
  const { threshold = 0.12, once = true, rootMargin = '0px 0px -40px 0px' } = options;
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    // If the browser lacks IntersectionObserver, just show it immediately.
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      node.classList.add('am-reveal-in');
      setRevealed(true);
      return undefined;
    }
    // Respect reduced-motion — reveal immediately, no animation.
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) {
      node.classList.add('am-reveal-in');
      setRevealed(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('am-reveal-in');
            setRevealed(true);
            if (once) io.unobserve(entry.target);
          } else if (!once) {
            entry.target.classList.remove('am-reveal-in');
          }
        });
      },
      { threshold, rootMargin },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold, once, rootMargin]);

  return { ref, revealed };
}

export default useRevealOnScroll;
